import { ConsentStatus } from '@remarkable/tracking-browser';

import * as Sentry from '@sentry/react';
import { tracker } from './analytics/tracker';

/**
 * The cookie consent is shared across multiple reMarkable applications.
 * Therefore, it should only be modified in sync with other apps.
 */
export const COOKIE_CONSENT_KEY_CROSS_APP = 'rmCookieConsent';
/**
 * - v1: initial consent version
 */
export const CURRENT_COOKIE_CONSENT_VERSION = 1;
export const COOKIE_CONSENT_DURATION = 1 * 365 * 24 * 60 * 60;
export enum ConsentLevel {
  NECESSARY = 'NECESSARY',
  ALL = 'ALL',
}

export interface ConsentCookie {
  time: string;
  level: ConsentLevel;
  version: number;
}

/**
 * Determines the cookie domain at runtime.
 * Prefers the build-time env var, but falls back to `.vercel-preview.rm.guru`
 * for preview deployments so the consent cookie is written to the shared
 * parent domain instead of the host-only exact subdomain.
 */
export const getCookieConsentDomain = (): string => {
  const envDomain = String(import.meta.env.PUBLIC_COOKIE_CONSENT_DOMAIN ?? '');
  if (envDomain) return envDomain;

  const VERCEL_PREVIEW_DOMAIN = '.vercel-preview.rm.guru';
  if (
    typeof window !== 'undefined' &&
    window.location.hostname.endsWith(VERCEL_PREVIEW_DOMAIN)
  ) {
    return VERCEL_PREVIEW_DOMAIN;
  }

  return '';
};

function isValidConsentLevel(
  level: string | null | undefined
): level is ConsentLevel {
  return Object.values(ConsentLevel).includes(
    level?.toUpperCase() as ConsentLevel
  );
}

export const getConsentFromCookie = (): ConsentCookie | null => {
  if (typeof document === 'undefined') return null;

  const match = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${COOKIE_CONSENT_KEY_CROSS_APP}=`));

  if (!match) return null;

  const raw = match.substring(COOKIE_CONSENT_KEY_CROSS_APP.length + 1);

  try {
    const consentCookie = JSON.parse(decodeURIComponent(raw)) as ConsentCookie;

    if (
      consentCookie.version >= CURRENT_COOKIE_CONSENT_VERSION &&
      !isValidConsentLevel(consentCookie.level)
    ) {
      Sentry.captureEvent({
        message: `Unknown cookie consent level: "${consentCookie.level as string}"`,
        level: 'warning',
        extra: {
          consentLevel: consentCookie.level,
          consentVersion: consentCookie.version,
          consentTime: consentCookie.time,
        },
      });
    }
    return {
      time: consentCookie.time,
      level: consentCookie.level.toUpperCase() as ConsentLevel,
      version: consentCookie.version,
    };
  } catch {
    return null;
  }
};

const setConsentCookie = (consent: ConsentCookie) => {
  if (typeof document === 'undefined') return;

  const encoded = encodeURIComponent(JSON.stringify(consent));
  const isSecure = window.location.protocol === 'https:';

  const parts = [
    `${COOKIE_CONSENT_KEY_CROSS_APP}=${encoded}`,
    `Path=/`,
    `Max-Age=${COOKIE_CONSENT_DURATION}`,
    `SameSite=${isSecure ? 'None' : 'Lax'}`,
  ];

  if (isSecure) {
    parts.push(`Secure`);

    // Only set Domain on HTTPS (production/preview), not on localhost
    const cookieDomain = getCookieConsentDomain();
    if (cookieDomain) {
      parts.push(`Domain=${cookieDomain}`);
    }
  }

  document.cookie = parts.join('; ');
};

export const hasSetConsent = (): boolean => {
  const consent = getConsentFromCookie();

  if (
    !consent ||
    !consent.version ||
    consent.version < CURRENT_COOKIE_CONSENT_VERSION ||
    !isValidConsentLevel(consent.level)
  ) {
    return false;
  }

  return true;
};

export const getConsentLevel = (): ConsentLevel => {
  try {
    const consent = getConsentFromCookie();
    return consent ? consent.level : ConsentLevel.NECESSARY;
  } catch (e) {
    console.error('[Consent]: Could not read cookie:', e);
    return ConsentLevel.NECESSARY;
  }
};

const applyConsent = (consentLevel: ConsentLevel) => {
  if (consentLevel === ConsentLevel.ALL) {
    tracker.updateUserConsentStatus(ConsentStatus.CONSENTED);
  } else {
    tracker.updateUserConsentStatus(ConsentStatus.REJECTED);
  }
};

export const initializeConsent = () => {
  const consentLevel = getConsentLevel();
  applyConsent(consentLevel);
};

export const updateConsentLevel = (newConsentLevel: ConsentLevel) => {
  const consent: ConsentCookie = {
    time: new Date().toISOString(),
    level: newConsentLevel,
    version: CURRENT_COOKIE_CONSENT_VERSION,
  };

  setConsentCookie(consent);
  applyConsent(newConsentLevel);
};

initializeConsent();
